import axios from "axios";
import mime from "mime";

type FileObj = {
  url?: string;
  type?: string;
  mimeType?: string;
};

export function isVideoFile(file: FileObj) {
  return (
    file.url?.includes(".mp4") ||
    file.type?.includes("video") ||
    file.mimeType?.includes("video")
  );
}

function blobToBase64(blob: Blob): Promise<string> {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }

      reject(new Error("Failed to read blob as a data URL"));
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader error"));
    reader.onabort = () => reject(new Error("FileReader aborted"));
    reader.readAsDataURL(blob);
  });
}

export async function getImageDimensions(
  file: string | Blob
): Promise<{ height: number; width: number }> {
  return new Promise(async (resolve) => {
    const img = new Image();

    img.onload = function () {
      const height = img.height;
      const width = img.width;

      resolve({
        height,
        width,
      });
    };

    img.src = typeof file === "string" ? file : await blobToBase64(file);
  });
}

export async function loadUrlAsBlob(
  url: string,
  { onProgress }: { onProgress(value: number): void }
) {
  const { data: blob } = await axios.get(url, {
    responseType: "blob",
    onDownloadProgress: ({ loaded, total }) => {
      onProgress((loaded / (total || loaded || 1)) * 100);
    },
  });
  return blob;
}

// TODO: seems like this loadBlob function does useless work
// since promise is resolved with the same blob object it gets as an argument
export function loadBlob(blob: File): Promise<Blob> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => {
      resolve(blob);
    };
  });
}

export function getImageFromFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.addEventListener("load", function () {
      if (typeof this.result === "string") {
        resolve(this.result);
        return;
      }

      reject(new Error("Failed to read file as a data URL"));
    });
    fileReader.addEventListener("error", function () {
      reject(this.error ?? new Error("FileReader error"));
    });
    fileReader.addEventListener("abort", function () {
      reject(new Error("FileReader aborted"));
    });
    fileReader.readAsDataURL(file);
  });
}

export function getFileTypeByName(
  name: string
): "video" | "image" | "document" | "audio" {
  const mimeType = mime.getType(name);
  // images *excluding gif* should be uploaded as images
  if (mimeType && mimeType.includes("image") && !mimeType.includes("gif")) {
    return "image";
    // GIFs and video files should be passed in as video
  } else if (
    mimeType &&
    (mimeType.includes("gif") || mimeType.includes("video"))
  ) {
    return "video";
  } else if (mimeType && mimeType.includes("audio")) {
    return "audio";
  } else if (mimeType && mimeType.includes("pdf")) {
    return "document";
  } else {
    throw new Error("Unsupported file type " + name + " " + mimeType);
  }
}

export function getFileExtension(filename: string) {
  return filename.split(".").pop();
}
